import { MaxUint256 } from "@uniswap/sdk-core"
import { maxLiquidityForAmounts } from "@uniswap/v3-sdk"

import type { TokenStore } from "@/domains/tokens/token-store"
import {
  getAmount0Delta,
  getAmount1Delta,
  getSqrtRatioAtTick,
  getTickAtSqrtRatio,
  toBigInt,
  toJSBI,
} from "@/domains/manual-cl-core"

interface CommonParams {
  /**
   * Current pool price as sqrtPriceX96 from `slot0()`. Preferred over
   * `getSqrtRatioAtTick(currentTick)` — when slot0 is available it encodes
   * the exact sub-tick price, avoiding rounding error that biases the
   * dependent-amount calculation in one direction.
   */
  sqrtPriceX96Current: bigint
  tickLower: number
  tickUpper: number
  token0: TokenStore
  token1: TokenStore
  fee: number
}

/**
 * Given an amount of token0 the user wants to deposit, derive the companion
 * amount of token1 that must be supplied to form a position with matching
 * liquidity. Bit-exact with Uniswap v3 via manual-cl-core math wrappers.
 *
 * Correctly handles single-sided (out-of-range) positions: if the position
 * is fully above range, `amount1` will be non-zero while `amount0` input is
 * ignored by Uniswap's SDK; we mirror that here by returning the computed
 * amount1 pair.
 */
export function calculateDependentAmount1FromAmount0(
  params: CommonParams & { amount0: bigint },
): bigint {
  if (params.amount0 <= 0n) return 0n
  const liquidity = getLiquidityForAmount0(params)
  return getMintAmountsForLiquidity({ ...params, liquidity }).amount1
}

/**
 * Inverse of `calculateDependentAmount1FromAmount0`.
 */
export function calculateDependentAmount0FromAmount1(
  params: CommonParams & { amount1: bigint },
): bigint {
  if (params.amount1 <= 0n) return 0n
  const liquidity = getLiquidityForAmount1(params)
  return getMintAmountsForLiquidity({ ...params, liquidity }).amount0
}

function getLiquidityForAmount0(params: CommonParams & { amount0: bigint }): bigint {
  const sqrtLower = getSqrtRatioAtTick(params.tickLower)
  const sqrtUpper = getSqrtRatioAtTick(params.tickUpper)
  return toBigInt(
    maxLiquidityForAmounts(
      toJSBI(params.sqrtPriceX96Current),
      toJSBI(sqrtLower),
      toJSBI(sqrtUpper),
      toJSBI(params.amount0),
      MaxUint256,
      true,
    ),
  )
}

function getLiquidityForAmount1(params: CommonParams & { amount1: bigint }): bigint {
  const sqrtLower = getSqrtRatioAtTick(params.tickLower)
  const sqrtUpper = getSqrtRatioAtTick(params.tickUpper)
  return toBigInt(
    maxLiquidityForAmounts(
      toJSBI(params.sqrtPriceX96Current),
      toJSBI(sqrtLower),
      toJSBI(sqrtUpper),
      MaxUint256,
      toJSBI(params.amount1),
      true,
    ),
  )
}

function getMintAmountsForLiquidity(
  params: CommonParams & { liquidity: bigint },
): { amount0: bigint; amount1: bigint } {
  const currentTick = getTickAtSqrtRatio(params.sqrtPriceX96Current)
  const sqrtLower = getSqrtRatioAtTick(params.tickLower)
  const sqrtUpper = getSqrtRatioAtTick(params.tickUpper)

  if (currentTick < params.tickLower) {
    return {
      amount0: getAmount0Delta({
        sqrtRatioAX96: sqrtLower,
        sqrtRatioBX96: sqrtUpper,
        liquidity: params.liquidity,
        roundUp: true,
      }),
      amount1: 0n,
    }
  }

  if (currentTick < params.tickUpper) {
    return {
      amount0: getAmount0Delta({
        sqrtRatioAX96: params.sqrtPriceX96Current,
        sqrtRatioBX96: sqrtUpper,
        liquidity: params.liquidity,
        roundUp: true,
      }),
      amount1: getAmount1Delta({
        sqrtRatioAX96: sqrtLower,
        sqrtRatioBX96: params.sqrtPriceX96Current,
        liquidity: params.liquidity,
        roundUp: true,
      }),
    }
  }

  return {
    amount0: 0n,
    amount1: getAmount1Delta({
      sqrtRatioAX96: sqrtLower,
      sqrtRatioBX96: sqrtUpper,
      liquidity: params.liquidity,
      roundUp: true,
    }),
  }
}
